import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Wrench, Settings, Zap, Sparkles, Search, Truck, Shield, ArrowRight } from "lucide-react";

const ServicesSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const services = [
    { id: 1, name: "Basic Service", icon: Wrench, price: "₹499", description: "Oil change, filter check", badge: null },
    { id: 2, name: "Premium Service", icon: Settings, price: "₹999", description: "Complete tune-up", badge: "Popular" },
    { id: 3, name: "Engine Repair", icon: Shield, price: "₹1499", description: "Fix engine issues", badge: null },
    { id: 4, name: "Brake Service", icon: Zap, price: "₹699", description: "Brake pad replacement", badge: null },
    { id: 5, name: "Bike Detailing", icon: Sparkles, price: "₹599", description: "Deep cleaning & polish", badge: null },
    { id: 6, name: "Inspection", icon: Search, price: "₹299", description: "Full safety check", badge: null },
    { id: 7, name: "Pickup/Drop", icon: Truck, price: "Free", description: "Doorstep service", badge: "Free" },
    { id: 8, name: "Full Care", icon: Star, price: "₹1999", description: "Complete package deal", badge: "Best" },
  ];

  const testimonials = [
    { id: 1, name: "Rohan Kulkarni", location: "Kothrud, Pune", rating: 5, initials: "RK", color: "#FF6B35", text: "Exceptional service! My bike feels brand new after their premium package. Transparent about every repair." },
    { id: 2, name: "Ananya Mehta", location: "Baner, Pune", rating: 5, initials: "AM", color: "#1C1C1C", text: "Best bike service in Pune. Free pickup and delivery made it so convenient. Highly recommend!" },
    { id: 3, name: "Vikram Deshmukh", location: "Viman Nagar, Pune", rating: 5, initials: "VD", color: "#2A2A2A", text: "Quick turnaround and fair pricing. The mechanics are knowledgeable and explain everything clearly." },
    { id: 4, name: "Neha Patwardhan", location: "Koregaon Park, Pune", rating: 5, initials: "NP", color: "#FF8C5A", text: "Impressed with quality and genuine parts. My daily commute is much smoother now. Great experience!" },
    { id: 5, name: "Arjun Patel", location: "Hinjewadi, Pune", rating: 5, initials: "AP", color: "#333333", text: "Professional team with attention to detail. They fixed issues I didn't even know existed. Worth every rupee!" },
  ];

  const stats = [
    { value: "5K+", label: "Happy Customers" },
    { value: "25+", label: "Service Centers" },
    { value: "4.9★", label: "Average Rating" },
    { value: "100%", label: "Genuine Parts" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07 } },
  };

  const cardItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <>
      {/* ── Services Grid ─────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20">
        {/* Section header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">
            What We Offer
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4 tracking-tight">
            Our Services
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto leading-relaxed">
            Professional bike care with transparent pricing and certified mechanics
          </p>
        </motion.div>

        <motion.div variants={container} initial="hidden" whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-16">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <motion.div key={service.id} variants={cardItem}>
                <Link to="/" className="group block relative bg-white border border-gray-200/80 rounded-2xl p-5 sm:p-6 hover:border-[#FF6B35]/40 hover:shadow-xl hover:shadow-[#FF6B35]/5 transition-all duration-300 hover:-translate-y-1">
                  {service.badge && (
                    <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold
                      ${service.badge === "Popular" ? "bg-[#FF6B35] text-white" :
                        service.badge === "Free" ? "bg-green-500 text-white" :
                          "bg-gray-900 text-white"}`}>
                      {service.badge}
                    </span>
                  )}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 bg-[#FF6B35]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#FF6B35] transition-all duration-300">
                      <Icon className="w-7 h-7 text-[#FF6B35] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{service.name}</h3>
                    <p className="text-xs text-gray-500 mb-3">{service.description}</p>
                    <span className="text-[#FF6B35] font-black text-base">{service.price}</span>
                  </div>
                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 group-hover:w-1/2 h-0.5 bg-[#FF6B35] rounded-full transition-all duration-300" />
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats strip */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div key={index}
              className="bg-[#111111] rounded-2xl p-6 text-center border border-white/[0.06] hover:border-[#FF6B35]/20 transition-colors group">
              <div className="text-2xl sm:text-3xl font-black text-[#FF6B35] mb-1 group-hover:scale-110 transition-transform origin-center inline-block">
                {stat.value}
              </div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ── Testimonials ──────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 relative overflow-hidden">
        {/* Dark background */}
        <div className="absolute inset-0 bg-[#0D0D0D]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        />
        {/* Orange glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[120px] opacity-5 pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">
              Reviews
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">Customer Stories</h2>
            <p className="text-gray-500">Real reviews from real riders in Pune</p>
          </motion.div>

          {/* Testimonial card */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div key={activeTestimonial}
                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#111111] border border-white/[0.08] rounded-3xl p-8 sm:p-10">

                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-lg sm:text-xl text-gray-200 leading-relaxed mb-8 font-medium">
                  "{testimonials[activeTestimonial].text}"
                </p>

                {/* Author */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: testimonials[activeTestimonial].color }}>
                      {testimonials[activeTestimonial].initials}
                    </div>
                    <div>
                      <p className="font-bold text-white">{testimonials[activeTestimonial].name}</p>
                      <p className="text-sm text-gray-500">{testimonials[activeTestimonial].location}</p>
                    </div>
                  </div>

                  {/* Nav arrows */}
                  <div className="flex gap-2">
                    <button onClick={() => setActiveTestimonial((p) => (p - 1 + testimonials.length) % testimonials.length)}
                      className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:bg-[#FF6B35]/10 hover:border-[#FF6B35]/30 text-gray-400 hover:text-[#FF6B35] transition-all">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => setActiveTestimonial((p) => (p + 1) % testimonials.length)}
                      className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:bg-[#FF6B35]/10 hover:border-[#FF6B35]/30 text-gray-400 hover:text-[#FF6B35] transition-all">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setActiveTestimonial(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${activeTestimonial === i ? "w-8 bg-[#FF6B35]" : "w-2 bg-white/20 hover:bg-white/40"}`} />
              ))}
            </div>
          </div>

          {/* CTA inside section */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="text-center mt-14">
            <Link to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6B35] hover:bg-[#E85520] text-white font-bold rounded-2xl transition-all hover:-translate-y-0.5 shadow-lg shadow-[#FF6B35]/25">
              Book Your Service Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServicesSection;
